import { queryOptions, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { apiClient } from '@cast/infrastructure-api';

const WOOP_WORKLOAD_GPU_METRICS_QUERY_KEY = 'woop-workload-gpu-metrics';

const getWorkloadGpuMetricsQueryKey = ({
  clusterId,
  workloadId,
  from,
  to,
}: {
  clusterId?: string;
  workloadId?: string;
  from?: string;
  to?: string;
} = {}) =>
  [
    WOOP_WORKLOAD_GPU_METRICS_QUERY_KEY,
    clusterId,
    workloadId,
    from,
    to,
  ] as const;

type UseWorkloadGpuMetricsQueryArgs = {
  clusterId: string;
  workloadId: string;
  from: string;
  to: string;
  enabled?: boolean;
};

const getRefetchInterval = (from: string, to: string) => {
  const length = dayjs(to).diff(from, 'milliseconds');
  if (!length) {
    return false;
  }
  const duration = dayjs.duration(length);
  if (duration.asMinutes() <= 15) {
    return dayjs.duration(1, 'minute').asMilliseconds();
  }
  if (duration.asHours() <= 1) {
    return dayjs.duration(5, 'minutes').asMilliseconds();
  }
  if (duration.asHours() <= 24) {
    return dayjs.duration(30, 'minutes').asMilliseconds();
  }
  if (duration.asHours() <= 7 * 24) {
    return dayjs.duration(1, 'hour').asMilliseconds();
  }
  return false;
};

const getWorkloadGpuMetricsQueryOptions = ({
  clusterId,
  workloadId,
  from,
  to,
  enabled,
}: UseWorkloadGpuMetricsQueryArgs) =>
  queryOptions({
    queryKey: getWorkloadGpuMetricsQueryKey({
      clusterId,
      workloadId,
      from,
      to,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.workloadOptimization.getWorkloadGpuMetrics({
          clusterId,
          workloadId,
          from,
          to,
        });
      return data;
    },
    enabled: enabled ?? !!(clusterId && workloadId && from && to),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: () => getRefetchInterval(from, to),
  });

export const useWorkloadGpuMetricsQuery = (
  args: UseWorkloadGpuMetricsQueryArgs
) => {
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getWorkloadGpuMetricsQueryOptions(args)
  );

  return {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
